import { faThumbsDown, faThumbsUp } from "@fortawesome/pro-regular-svg-icons";
import {
  faThumbsDown as faThumbsDownSolid,
  faThumbsUp as faThumbsUpSolid,
} from "@fortawesome/pro-solid-svg-icons";
import { useMemo, useState } from "react";

import { Icon } from "src/base-components/Icon";
import { Tooltip } from "src/base-components/Tooltip";
import {
  ConversationMessage,
  ConversationMessageRoleEnum,
} from "src/clients/copilot";
import { BeMappedNode } from "src/constants/NodeDataTypes";
import { NodeSuggestion } from "src/copilot/CodeNodeSuggestion";
import { replaceNodeSuggestions } from "src/copilot/utils";
import {
  tracker,
  trackingEvents,
} from "src/instrumentation/customTrackingEvents";
import { useAuthoringContext } from "src/router/routerContextHooks";

type ReactionStatus = "liked" | "disliked";

const Actions: React.FC<{
  status: ReactionStatus | null;
  onLike: VoidFunction;
  onDislike: VoidFunction;
}> = ({ status, onLike, onDislike }) => (
  <div className="flex items-center gap-x-1">
    <Tooltip placement="top" title="Good response" asChild>
      <Icon
        color="text-gray-500"
        icon={status === "liked" ? faThumbsUpSolid : faThumbsUp}
        size="xs"
        onClick={onLike}
      />
    </Tooltip>
    <Tooltip placement="top" title="Not good response" asChild>
      <Icon
        color="text-gray-500"
        icon={status === "disliked" ? faThumbsDownSolid : faThumbsDown}
        size="xs"
        onClick={onDislike}
      />
    </Tooltip>
  </div>
);

type Props = {
  message: ConversationMessage;
  node: BeMappedNode;
  conversationId: string;
  completionId: string;
  onApply: (suggestion: NodeSuggestion) => void;
};

export const ConversationMessageItem: React.FC<Props> = ({
  message,
  node,
  conversationId,
  completionId,
  onApply,
}) => {
  const { orgId } = useAuthoringContext();
  const [reactionStatus, setReaction] = useState<ReactionStatus | null>(null);
  const isCopilotMessage = message.role === ConversationMessageRoleEnum.COPILOT;
  const renderedContent = useMemo(
    () =>
      replaceNodeSuggestions({
        content: message.content,
        originalNode: node,
        onApply,
      }),
    [message.content, node, onApply],
  );

  const handleReaction = (status: ReactionStatus) => {
    let recordedReaction;
    if (status === reactionStatus) {
      setReaction(null);
      recordedReaction = "unset" as const;
    } else {
      setReaction(status);
      recordedReaction = status;
    }

    tracker.emit(
      trackingEvents.reactCopilotResponse({
        organization_id: orgId,
        conversation_id: conversationId,
        completion_id: completionId,
        reaction: recordedReaction,
      }),
    );
  };

  return (
    <li className="flex flex-col gap-y-2">
      <div className="flex items-center justify-between gap-x-4">
        <span className="text-gray-500 font-inter-normal-12px">
          {isCopilotMessage ? "Copilot" : "User"}
        </span>
        {isCopilotMessage && (
          <Actions
            status={reactionStatus}
            onDislike={() => handleReaction("disliked")}
            onLike={() => handleReaction("liked")}
          />
        )}
      </div>
      <div>{renderedContent}</div>
    </li>
  );
};
